import { useMemo, useState, type ReactNode } from 'react';
import { Compass, X, type LucideIcon } from 'lucide-react';
import {
  NATIVE_QUICK_ACTIONS,
  findNativeQuickAction,
  type NativeQuickActionChoice,
  type QuickActionCategory,
} from '@hector/shared-types/quick-actions';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { QuickActionsGrid } from './QuickActionsGrid';
import { iconForPrompt } from './quickActionIcons';
import {
  isNativePrompt,
  promptCategory,
  type CreateUserPromptInput,
  type UserPrompt,
} from '../../api/userPrompts.api';

const DISCOVERY_DISMISSED_KEY = 'hector.assistant.discoveryQuickActionDismissed.v1';

type CaseOption = { id: string; title: string };

type QuickActionsPanelProps = {
  // Bibliothèque complète (les 3 colonnes), lue avec ?include=categories.
  prompts: UserPrompt[];
  promptsLoading: boolean;
  onCreatePrompt: (data: CreateUserPromptInput) => Promise<UserPrompt>;
  onUpdatePrompt: (id: string, data: { title?: string; description?: string; prompt_content?: string }) => Promise<UserPrompt>;
  onSetPromptHidden: (id: string, hidden: boolean) => Promise<UserPrompt>;
  onDeletePrompt: (id: string) => Promise<void>;
  onReorderPrompts: (items: { id: string; sort_order: number }[]) => Promise<void>;

  // Préremplit le composer (jamais d'envoi automatique — même contrat que pendingPrompt)
  onPromptClick: (promptContent: string) => void;

  // Contexte dossier — les actions "Rédaction d'actes" et "Gestion de dossier"
  // en ont besoin ; si aucun n'est attaché, un sélecteur inline propose d'en choisir un.
  cases: CaseOption[];
  selectedCaseId: string | null;
  onCaseSelect: (caseId: string | null) => void;
};

// Ordre des colonnes voulu par le produit (2026-09-25) : gestion du dossier,
// puis analyse, puis rédaction.
const COLUMNS: readonly { category: QuickActionCategory; title: string }[] = [
  { category: 'dossier', title: 'Gestion de dossier' },
  { category: 'analyse', title: 'Analyse' },
  { category: 'redaction', title: "Rédaction d'actes" },
];

/** Colonnes dont chaque action porte sur un dossier (sélecteur de dossier à la volée). */
const CASE_SCOPED_CATEGORIES = new Set<QuickActionCategory>(['redaction', 'dossier']);

/**
 * Actions Analyse par défaut, des plus utilisées aux moins utilisées : envois en
 * production des 30 derniers jours au 2026-09-25 (messages d'avocats clients
 * commençant par le texte de l'action, hors Word), avocats distincts sur 90 jours
 * entre parenthèses. Synthèse 192 (82), Arguments 118 (96), Répondre aux arguments
 * adverses 115 (95), Jurisprudence 24 (27), Vigilance 19 (22), Mail client 11 (10),
 * Sources 6 (9), Tableau 5 (10). Tableau était affiché en 3e jusqu'ici, sans usage.
 */
const ANALYSE_TITLES_BY_USAGE = [
  'Synthèse du dossier',
  'Arguments pour mon client',
  'Répondre aux arguments adverses',
  'Rechercher de la jurisprudence',
  'Points de vigilance',
  'Rédiger un mail client',
  'Vérifier mes sources',
  'Tableau récapitulatif des pièces',
];

/** Cartes visibles d'un coup par colonne ; au-delà, la colonne défile. */
const VISIBLE_CARDS_PER_COLUMN = 3;

/**
 * Toutes les icônes du panneau Assistant sont uniformisées en noir (décision
 * produit du 2026-09-09) ; le dock dossier garde, lui, ses couleurs par action.
 */
function iconPastille(Icon: LucideIcon) {
  return (
    <span
      aria-hidden="true"
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-grey-100"
    >
      <Icon className="h-4 w-4 text-charcoal" />
    </span>
  );
}

function cardClasses() {
  return 'group relative flex w-full items-center gap-3 rounded-lg border border-grey-200 bg-white px-3 py-2 text-left transition-colors hover:border-grey-300 hover:bg-grey-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal focus-visible:ring-offset-2';
}

/**
 * Icône + libellé + description, chacun tronqué sur une ligne (`truncate`) —
 * c'est ce qui garde toutes les cartes à hauteur identique quel que soit la
 * longueur du texte, donc alignées entre les 3 colonnes (retour du 2026-09-10 :
 * la version sans description, en plus d'être moins lisible, désalignait les
 * lignes car chaque libellé wrappait différemment).
 */
function CardBody({ icon, label, description }: { icon: LucideIcon; label: string; description: string }) {
  return (
    <>
      {iconPastille(icon)}
      <span className="min-w-0">
        <span className="block truncate text-xs font-medium text-charcoal">{label}</span>
        <span className="mt-0.5 block truncate text-xs text-grey-500">{description}</span>
      </span>
    </>
  );
}

/**
 * Sélecteur de dossier inline — ouvert quand une action liée au dossier est
 * cliquée sans conversation déjà attachée à un dossier ("toujours visibles,
 * sélection à la volée", décision produit du 2026-09-09). Choisir un dossier
 * l'attache à la conversation ET déclenche l'action dans la foulée.
 */
function CasePickerPopover({
  cases,
  onPick,
  children,
}: {
  cases: CaseOption[];
  onPick: (caseId: string) => void;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const filtered = useMemo(
    () => cases.filter((c) => c.title.toLowerCase().includes(query.toLowerCase())),
    [cases, query],
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-1.5">
        <p className="px-2 pb-1.5 pt-1 text-xs font-semibold text-grey-500">
          Choisir le dossier concerné
        </p>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher un dossier..."
          className="mb-1 w-full rounded-md border border-grey-200 px-2 py-1 text-sm outline-none focus:border-grey-400"
        />
        <div className="max-h-56 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="px-2 py-2 text-xs text-grey-400">Aucun dossier trouvé.</p>
          ) : (
            filtered.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => { setOpen(false); onPick(c.id); }}
                className="w-full truncate rounded-md px-2 py-1.5 text-left text-sm text-charcoal hover:bg-grey-50"
              >
                {c.title}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function DossierScopedCard({
  icon,
  label,
  description,
  prompt,
  hasCase,
  cases,
  onPick,
  onPromptClick,
}: {
  icon: LucideIcon;
  label: string;
  description: string;
  prompt: string;
  hasCase: boolean;
  cases: CaseOption[];
  onPick: (caseId: string) => void;
  onPromptClick: (prompt: string) => void;
}) {
  if (!hasCase) {
    return (
      <CasePickerPopover cases={cases} onPick={onPick}>
        <button type="button" className={cardClasses()}>
          <CardBody icon={icon} label={label} description={description} />
        </button>
      </CasePickerPopover>
    );
  }
  return (
    <button type="button" onClick={() => onPromptClick(prompt)} className={cardClasses()}>
      <CardBody icon={icon} label={label} description={description} />
    </button>
  );
}

/**
 * Carte « Rédiger un acte… » (native `redact-acte`) : ouvre le choix du type
 * d'acte au lieu d'envoyer un prompt unique. Les choix viennent du catalogue
 * natif partagé, qui vérifie par test que chaque type existe dans le
 * catalogue d'actes.
 */
function ActeSelectorCard({
  icon,
  label,
  description,
  choices,
  hasCase,
  cases,
  onPick,
  onPromptClick,
}: {
  icon: LucideIcon;
  label: string;
  description: string;
  choices: readonly NativeQuickActionChoice[];
  hasCase: boolean;
  cases: CaseOption[];
  onPick: (caseId: string) => void;
  onPromptClick: (prompt: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [reopenAfterCasePick, setReopenAfterCasePick] = useState(false);

  if (!hasCase) {
    // Sans dossier : choisir le dossier d'abord, puis rouvrir le sélecteur de type d'acte.
    return (
      <CasePickerPopover
        cases={cases}
        onPick={(caseId) => {
          onPick(caseId);
          setReopenAfterCasePick(true);
        }}
      >
        <button type="button" className={cardClasses()}>
          <CardBody icon={icon} label={label} description={description} />
        </button>
      </CasePickerPopover>
    );
  }

  return (
    <Popover
      open={open || reopenAfterCasePick}
      onOpenChange={(next) => { setOpen(next); if (!next) setReopenAfterCasePick(false); }}
    >
      <PopoverTrigger asChild>
        <button type="button" className={cardClasses()}>
          <CardBody icon={icon} label={label} description={description} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-1.5">
        <p className="px-2 pb-1.5 pt-1 text-xs font-semibold text-grey-500">Choisir le type d'acte</p>
        <div className="flex flex-col">
          {choices.map((choice) => (
            <button
              key={choice.label}
              type="button"
              onClick={() => { setOpen(false); setReopenAfterCasePick(false); onPromptClick(choice.prompt); }}
              className="rounded-md px-2 py-1.5 text-left text-sm text-charcoal hover:bg-grey-50"
            >
              {choice.label}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Consignes chargées dans le composer au clic sur "Découvrir Hector".
 * Volontairement posé dans le champ de saisie (via onPromptClick →
 * setPendingPrompt) et NON envoyé : l'avocat relit et déclenche lui-même,
 * comme pour toutes les autres actions rapides (demande du 2026-09-25).
 * Le prompt demande un tour d'horizon ancré sur les capacités réelles —
 * analyse de dossier, rédaction d'actes, gestion des pièces — pour éviter
 * que l'agent improvise un catalogue de fonctions qui n'existent pas.
 */
const DISCOVERY_PROMPT = `Fais-moi découvrir ce que tu sais faire. Présente-moi les grandes familles de tâches que tu peux prendre en charge : l'analyse d'un dossier et de ses pièces, la rédaction d'actes, la recherche de jurisprudence et la gestion des pièces et bordereaux. Pour chacune, donne un exemple concret de demande que je peux te formuler, en une phrase. Reste bref et concret, sans jargon technique. Termine en me demandant sur quel dossier je veux commencer.`;

function DiscoveryCard({ onStart, onDismiss }: { onStart: () => void; onDismiss: () => void }) {
  return (
    <div className="group relative mx-auto flex w-full max-w-md items-center gap-3 rounded-lg border border-grey-200 bg-white px-3 py-2 transition-colors hover:border-grey-300 hover:bg-grey-50">
      {/* Zone cliquable en calque : couvre la carte sans imbriquer le bouton
          "masquer" dans un autre bouton (HTML invalide, clic avalé). */}
      <button
        type="button"
        onClick={onStart}
        aria-label="Découvrir Hector"
        className="absolute inset-0 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
      />
      <span
        aria-hidden="true"
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-grey-100"
      >
        <Compass className="h-4 w-4 text-charcoal" />
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-1.5">
          <span className="block truncate text-xs font-medium text-charcoal">Découvrir Hector</span>
          <span className="rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700">
            Nouveau
          </span>
        </span>
        <span className="mt-0.5 block truncate text-xs text-grey-500">
          Un tour rapide de ce que je peux faire pour vous
        </span>
      </span>
      <button
        type="button"
        aria-label="Masquer cette suggestion"
        onClick={onDismiss}
        className="relative z-10 ml-auto shrink-0 rounded-full p-1 text-grey-400 opacity-0 transition-opacity hover:bg-grey-100 hover:text-grey-600 group-hover:opacity-100"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/**
 * Actions natives affichables sans le serveur : lignes fictives tirées du
 * catalogue, jamais persistées (ids préfixés `native:`). Servent de repli en
 * lecture seule quand la bibliothèque ne contient aucune native — backend
 * antérieur à la migration 175 (fenêtre de déploiement) ou erreur de
 * chargement — pour que les colonnes Rédaction et Dossier ne soient jamais vides.
 * Bornées aux premières cartes : ce repli est une grille simple, sans le
 * défilement de `QuickActionsGrid`, et la colonne Dossier compte huit natives.
 */
function catalogFallbackPrompts(category: QuickActionCategory): UserPrompt[] {
  return NATIVE_QUICK_ACTIONS
    .filter((native) => native.category === category && !native.initiallyHidden)
    .slice(0, VISIBLE_CARDS_PER_COLUMN)
    .map((native, index) => ({
      id: `native:${native.key}`,
      title: native.title,
      description: native.description,
      prompt_content: native.prompt,
      sort_order: index,
      is_default: true,
      created_at: '',
      updated_at: '',
      category: native.category,
      native_key: native.key,
      is_hidden: false,
    }));
}

/**
 * Panneau "actions rapides" de l'espace Assistant général (/assistant, écran
 * d'accueil sans conversation) : 3 colonnes — Rédaction d'actes, Gestion de
 * dossier, Analyse — toutes lues dans la bibliothèque de l'avocat
 * (`user_prompt_library.category`, migration 175). Chaque colonne a son
 * « Gérer » et son « Ajouter une action » ; les actions natives Hector s'y
 * masquent et s'y réordonnent mais ne se modifient pas (verrou serveur).
 *
 * "Toujours visibles, sélection de dossier à la volée" (décision du
 * 2026-09-09) : les actions Rédaction/Dossier restent affichées même sans
 * dossier attaché ; les cliquer sans dossier ouvre CasePickerPopover, qui
 * attache le dossier choisi à la conversation puis déclenche l'action.
 */
export function QuickActionsPanel({
  prompts,
  promptsLoading,
  onCreatePrompt,
  onUpdatePrompt,
  onSetPromptHidden,
  onDeletePrompt,
  onReorderPrompts,
  onPromptClick,
  cases,
  selectedCaseId,
  onCaseSelect,
}: QuickActionsPanelProps) {
  const [discoveryDismissed, setDiscoveryDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISCOVERY_DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  const dismissDiscovery = () => {
    setDiscoveryDismissed(true);
    try {
      localStorage.setItem(DISCOVERY_DISMISSED_KEY, '1');
    } catch {
      // localStorage indisponible (navigation privée, quota) : la carte reste
      // masquée pour cette session seulement, ce n'est pas bloquant.
    }
  };

  const hasCase = !!selectedCaseId;

  const attachCaseThenRun = (caseId: string, prompt: string) => {
    onCaseSelect(caseId);
    onPromptClick(prompt);
  };

  // Mémoïsé : chaque grille resynchronise son état local quand l'identité de
  // son tableau change — un nouveau tableau à chaque rendu relancerait l'effet.
  const promptsByCategory = useMemo(() => {
    const grouped: Record<QuickActionCategory, UserPrompt[]> = { redaction: [], dossier: [], analyse: [] };
    for (const prompt of prompts) grouped[promptCategory(prompt)].push(prompt);
    return grouped;
  }, [prompts]);

  // Une bibliothèque servie par le backend actuel contient toujours ses
  // natives (amorçage serveur) : leur absence signale un backend antérieur ou
  // un échec de chargement.
  const nativesUnavailable = !promptsLoading && !prompts.some(isNativePrompt);

  const renderCaseScopedCard = (prompt: UserPrompt) => {
    const choices = findNativeQuickAction(prompt.native_key)?.choices;
    if (choices) {
      return (
        <ActeSelectorCard
          icon={iconForPrompt(prompt)}
          label={prompt.title}
          description={prompt.description ?? ''}
          choices={choices}
          hasCase={hasCase}
          cases={cases}
          onPick={(caseId) => onCaseSelect(caseId)}
          onPromptClick={onPromptClick}
        />
      );
    }
    return (
      <DossierScopedCard
        icon={iconForPrompt(prompt)}
        label={prompt.title}
        description={prompt.description ?? ''}
        prompt={prompt.prompt_content}
        hasCase={hasCase}
        cases={cases}
        onPick={(caseId) => attachCaseThenRun(caseId, prompt.prompt_content)}
        onPromptClick={onPromptClick}
      />
    );
  };

  return (
    <div className="mt-6 flex w-full max-w-4xl flex-col gap-4">
      {!discoveryDismissed && (
        <DiscoveryCard
          onStart={() => onPromptClick(DISCOVERY_PROMPT)}
          onDismiss={dismissDiscovery}
        />
      )}

      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        {COLUMNS.map(({ category, title }) => {
          const caseScoped = CASE_SCOPED_CATEGORIES.has(category);

          if (caseScoped && nativesUnavailable) {
            return (
              <div key={category} className="flex flex-col" data-quick-action-column={category}>
                <span className="mb-2 flex h-4 items-center text-xs font-semibold uppercase tracking-wider text-grey-400">
                  {title}
                </span>
                <div className="grid grid-cols-1 gap-2">
                  {catalogFallbackPrompts(category).map((prompt) => (
                    <div key={prompt.id}>{renderCaseScopedCard(prompt)}</div>
                  ))}
                </div>
              </div>
            );
          }

          return (
            <QuickActionsGrid
              key={category}
              title={title}
              category={category}
              prompts={promptsByCategory[category]}
              isLoading={promptsLoading}
              onPromptClick={onPromptClick}
              onDeletePrompt={onDeletePrompt}
              onReorderPrompts={onReorderPrompts}
              onCreatePrompt={onCreatePrompt}
              onUpdatePrompt={onUpdatePrompt}
              onSetPromptHidden={onSetPromptHidden}
              visibleCardCount={VISIBLE_CARDS_PER_COLUMN}
              priorityTitles={category === 'analyse' ? ANALYSE_TITLES_BY_USAGE : undefined}
              renderCollapsedCard={caseScoped ? renderCaseScopedCard : undefined}
            />
          );
        })}
      </div>
    </div>
  );
}
